import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Activity, TrendingUp, Users } from 'lucide-react';
import Chart from './Chart';
import SearchableDropdown from './SearchableDropdown';

const MonthlyTrendsRenderer = ({ monthlyTrends, loading = false }) => {
  const [selectedUnit, setSelectedUnit] = useState('TK05');
  const [mobileChartIndex, setMobileChartIndex] = useState(0);
  const chartsCarouselRef = useRef(null);

  // When monthlyTrends loads with units, ensure selectedUnit is in the list; otherwise pick first unit so all units are visible
  useEffect(() => {
    const units = monthlyTrends?.unit_highlights?.map((u) => u.unit_name) || Object.keys(monthlyTrends?.unit_monthly_data || {});
    if (units.length > 0 && !units.includes(selectedUnit)) {
      setSelectedUnit(units[0]);
    }
  }, [monthlyTrends?.unit_highlights, monthlyTrends?.unit_monthly_data, selectedUnit]);

  useEffect(() => {
    // Clear the cache for monthly trends to ensure we get the updated date range
    // but only once per mount to avoid infinite loops if it triggered a reload
    localStorage.removeItem('monthly_trends_cache');

    // Also clear stats and chart caches which might hold old/broken data
    localStorage.removeItem('stats_cache');
    localStorage.removeItem('chart_unit_issues_cache');
    localStorage.removeItem('chart_monthly_trend_cache');
  }, []);

  // Fallback data when backend returns empty or error
  const fallbackData = {
    monthly_averages: {
      "1": { avg_issues_per_unit: 15.2, trend: "stable" },
      "2": { avg_issues_per_unit: 14.8, trend: "decreasing" },
      "3": { avg_issues_per_unit: 16.1, trend: "increasing" },
      "4": { avg_issues_per_unit: 15.5, trend: "stable" },
      "5": { avg_issues_per_unit: 14.9, trend: "decreasing" },
      "6": { avg_issues_per_unit: 15.7, trend: "stable" }
    },
    unit_highlights: [
      { unit_name: "TK05", current_month_issues: 12, monthly_average: 15.4, change_percentage: -5.2, performance: "above_average" },
      { unit_name: "TK17", current_month_issues: 18, monthly_average: 16.2, change_percentage: 3.1, performance: "below_average" },
      { unit_name: "TK14", current_month_issues: 14, monthly_average: 14.8, change_percentage: -1.5, performance: "above_average" }
    ],
    summary: {
      improving_units: 12,
      declining_units: 8,
      stable_units: 34
    }
  };

  // Show loading or initializing state
  if (loading || (monthlyTrends && monthlyTrends.initializing)) {
    const progress = monthlyTrends?.overall_progress || 0;
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-900 font-medium">
            {monthlyTrends?.initializing ? "System Initializing..." : "Analyzing monthly trends..."}
          </p>
          {monthlyTrends?.initializing && (
            <div className="w-64 bg-gray-200 rounded-full h-2.5 mt-4 mx-auto">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
              <p className="text-xs text-gray-500 mt-2">{progress}% completed</p>
            </div>
          )}
          <p className="text-sm text-gray-500 mt-2">
            {monthlyTrends?.initializing
              ? "Loading maintenance records into high-speed memory..."
              : "This may take a few moments for large datasets"}
          </p>
        </div>
      </div>
    );
  }

  // Use fallback data if backend returns empty or error
  const data = (!monthlyTrends || monthlyTrends.error ||
    (!monthlyTrends.unit_highlights || monthlyTrends.unit_highlights.length === 0))
    ? fallbackData : monthlyTrends;

  // Helper function to generate month labels with validation
  const generateMonthLabels = (unitData) => {
    const monthLabels = [];

    if (unitData && unitData.period_start && unitData.period_end) {
      // Use the actual data period from backend
      const startParts = unitData.period_start.split('-');
      const endParts = unitData.period_end.split('-');

      // Validate that we have valid year-month pairs
      if (startParts.length === 2 && endParts.length === 2) {
        const startYear = parseInt(startParts[0], 10);
        const startMonth = parseInt(startParts[1], 10);
        const endYear = parseInt(endParts[0], 10);
        const endMonth = parseInt(endParts[1], 10);

        // Validate parsed values are valid numbers and reasonable dates
        if (!isNaN(startYear) && !isNaN(startMonth) && !isNaN(endYear) && !isNaN(endMonth) &&
          startYear >= 2000 && startYear <= 2100 && startMonth >= 1 && startMonth <= 12 &&
          endYear >= 2000 && endYear <= 2100 && endMonth >= 1 && endMonth <= 12) {

          // Generate labels from start to end period in YYYY-MM format
          let currentYear = startYear;
          let currentMonth = startMonth;

          while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
            // Use YYYY-MM format for consistency with other charts
            monthLabels.push(`${currentYear}-${String(currentMonth).padStart(2, '0')}`);

            currentMonth++;
            if (currentMonth > 12) {
              currentMonth = 1;
              currentYear++;
            }
          }
        } else {
          // Invalid period data, fall through to fallback
          console.warn(`Invalid period data: ${unitData.period_start} to ${unitData.period_end}`);
        }
      } else {
        // Invalid format, fall through to fallback
        console.warn(`Invalid period format: ${unitData.period_start} to ${unitData.period_end}`);
      }
    }

    // If no labels were generated, use fallback
    if (monthLabels.length === 0) {
      // Fallback to current system approach if backend data not available
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1; // getMonth() is 0-indexed
      const currentYear = currentDate.getFullYear();

      for (let i = 11; i >= 0; i--) {
        let targetMonth = currentMonth - i;
        let targetYear = currentYear;
        while (targetMonth <= 0) {
          targetMonth += 12;
          targetYear--;
        }
        // Use YYYY-MM format for consistency
        monthLabels.push(`${targetYear}-${String(targetMonth).padStart(2, '0')}`);
      }
    }

    return monthLabels;
  };

  // Generate chart data for individual unit vs its yearly average
  const getIndividualUnitChartData = () => {
    const selectedUnitData = data.unit_highlights.find(unit => unit.unit_name === selectedUnit);
    if (!selectedUnitData) return null;

    // Generate month labels based on backend data period instead of system date
    const unitData = data.unit_monthly_data?.[selectedUnit];
    const monthLabels = generateMonthLabels(unitData);

    // Get real unit monthly data from backend (already in rolling 12-month format)
    const unitMonthlyData = data.unit_monthly_data?.[selectedUnit]?.monthly_issues || [];
    const unitYearlyAverage = data.unit_monthly_data?.[selectedUnit]?.yearly_average || selectedUnitData.monthly_average;

    // Use the rolling data directly from backend - use actual data regardless of length
    // If we have some data but not for this unit, don't just show a flat line
    const unitMonthlyIssues = unitMonthlyData.length > 0 ? unitMonthlyData.map(val => parseFloat(val)) :
      (data.unit_monthly_data && Object.keys(data.unit_monthly_data).length > 0) ? null :
        monthLabels.map(() => parseFloat(unitYearlyAverage));

    // If unitMonthlyIssues is null, it means data exists for other units but not this one
    // Also check if we're using fallback data (all values are the same = flat line)
    if (unitMonthlyIssues === null || 
        (!unitData && !data.unit_monthly_data) ||
        (Array.isArray(unitMonthlyIssues) && unitMonthlyIssues.every(val => val === unitMonthlyIssues[0]))) {
      return {
        isEmpty: true,
        message: unitData ? `No detailed monthly data available for ${selectedUnit} in the selected period.` : `Loading data for ${selectedUnit}...`
      };
    }

    // Unit's yearly average (constant line)
    const unitYearlyLine = monthLabels.map(() => parseFloat(unitYearlyAverage));

    return {
      type: "line",
      data: {
        labels: monthLabels,
        datasets: [
          {
            label: `${selectedUnit} Monthly Events`,
            data: unitMonthlyIssues,
            borderColor: "#EF4444", // Modern Red
            backgroundColor: "rgba(239, 68, 68, 0.1)",
            tension: 0.4,
            fill: true, // Modern area fill
            borderWidth: 3,
            pointRadius: 4,
            pointBackgroundColor: "#EF4444",
            pointHoverRadius: 6,
            zIndex: 10
          },
          {
            label: "Unit Yearly Baseline",
            data: unitYearlyLine,
            borderColor: "#10B981", // Modern Emerald
            backgroundColor: "rgba(16, 185, 129, 0.05)",
            tension: 0, // Keep average line straight
            fill: false,
            borderWidth: 2,
            borderDash: [5, 5],
            pointRadius: 0,
            zIndex: 5
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index',
        },
        plugins: {
          title: {
            display: false
          },
          legend: {
            position: "top",
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              boxWidth: 10,
              boxHeight: 10,
              padding: 16,
              color: '#334155',
              font: {
                size: 13,
                weight: '600'
              }
            }
          },
          tooltip: {
            padding: 12,
            borderRadius: 8,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            titleColor: '#1E293B',
            bodyColor: '#475569',
            borderColor: '#E2E8F0',
            borderWidth: 1,
            bodySpacing: 8,
            usePointStyle: true
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              display: true,
              color: 'rgba(226, 232, 240, 0.5)',
              drawBorder: false
            },
            ticks: {
              padding: 10
            }
          },
          x: {
            grid: {
              display: false
            },
            ticks: {
              padding: 8,
              maxTicksLimit: 10,
              autoSkip: true,
              maxRotation: 45,
              minRotation: 0,
              font: { size: 10 }
            }
          }
        }
      }
    };
  };

  // Generate chart data for unit vs fleet yearly average
  const getFleetComparisonChartData = () => {
    const selectedUnitData = data.unit_highlights.find(unit => unit.unit_name === selectedUnit);
    if (!selectedUnitData) return null;

    // Generate month labels based on backend data period instead of system date
    const unitData = data.unit_monthly_data?.[selectedUnit];
    const monthLabels = generateMonthLabels(unitData);

    // Fleet yearly average (constant line) from backend summary or fallback
    const fleetYearlyAverage = parseFloat(data.summary?.fleet_average) || 15.4;

    // Get real unit monthly data from backend (already in rolling 12-month format)
    const unitMonthlyData = unitData?.monthly_issues || [];
    const unitYearlyAverage = unitData?.yearly_average || selectedUnitData.monthly_average;

    // Unit's monthly issues (Main line)
    const unitMonthlyIssues = unitMonthlyData.length > 0 ? unitMonthlyData.map(val => parseFloat(val)) :
      monthLabels.map(() => parseFloat(unitYearlyAverage));

    // Check if we're using fallback data (flat line) - indicates data not loaded
    const isUsingFallbackData = !unitData || unitMonthlyData.length === 0;
    const allValuesSame = Array.isArray(unitMonthlyIssues) && 
      unitMonthlyIssues.every((val, i, arr) => i === 0 || val === arr[0]);

    if (isUsingFallbackData && allValuesSame && !data.unit_monthly_data) {
      return {
        isEmpty: true,
        message: `Loading data for ${selectedUnit}...`
      };
    }

    // Fleet monthly average (Secondary line) - Now dynamic instead of constant line!
    const fleetMonthlyData = data.monthly_averages || {};
    const fleetMonthlyIssues = monthLabels.map(label => {
      // monthly_averages is keyed by "YYYY-MM"
      return fleetMonthlyData[label]?.avg_issues_per_unit || fleetYearlyAverage;
    });

    return {
      type: "line",
      data: {
        labels: monthLabels,
        datasets: [
          {
            label: `${selectedUnit} Monthly Events`,
            data: unitMonthlyIssues,
            borderColor: "#3B82F6", // Modern Blue
            backgroundColor: "rgba(59, 130, 246, 0.1)",
            tension: 0.4,
            fill: true, // Modern area fill
            borderWidth: 3,
            pointRadius: 4,
            pointBackgroundColor: "#3B82F6",
            pointHoverRadius: 6,
            zIndex: 10
          },
          {
            label: "Fleet Monthly Baseline",
            data: fleetMonthlyIssues,
            borderColor: "#94A3B8", // Modern Slate
            backgroundColor: "rgba(148, 163, 184, 0.05)",
            tension: 0.4, // Smooth curve
            fill: true, // Area fill for fleet too but more subtle
            borderWidth: 2,
            borderDash: [5, 5],
            pointRadius: 0, // Hide points for average line
            zIndex: 5
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index',
        },
        plugins: {
          title: {
            display: false
          },
          legend: {
            position: "top",
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              boxWidth: 10,
              boxHeight: 10,
              padding: 16,
              color: '#334155',
              font: {
                size: 13,
                weight: '600'
              }
            }
          },
          tooltip: {
            padding: 12,
            borderRadius: 8,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            titleColor: '#1E293B',
            bodyColor: '#475569',
            borderColor: '#E2E8F0',
            borderWidth: 1,
            bodySpacing: 8,
            usePointStyle: true
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              display: true,
              color: 'rgba(226, 232, 240, 0.5)',
              drawBorder: false
            },
            ticks: {
              padding: 10
            }
          },
          x: {
            grid: {
              display: false
            },
            ticks: {
              padding: 8,
              maxTicksLimit: 10,
              autoSkip: true,
              maxRotation: 45,
              minRotation: 0,
              font: { size: 10 }
            }
          }
        }
      }
    };
  };

  const getChartStep = (container) => {
    if (!container) return 1;
    const firstSlide = container.querySelector('[data-carousel-item="monthly-chart"]');
    if (!firstSlide) return Math.max(container.clientWidth, 1);
    const styles = window.getComputedStyle(container);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return Math.max(firstSlide.getBoundingClientRect().width + gap, 1);
  };

  const handleChartsCarouselScroll = (e) => {
    const container = e.currentTarget;
    const step = getChartStep(container);
    const idx = Math.round(container.scrollLeft / step);
    setMobileChartIndex(Math.max(0, Math.min(1, idx)));
  };

  const scrollToChart = (index) => {
    const container = chartsCarouselRef.current;
    if (!container) return;
    const step = getChartStep(container);
    container.scrollTo({ left: step * index, behavior: 'smooth' });
    setMobileChartIndex(index);
  };

  const renderIndividualPerformanceCard = (className = '') => (
    <div className={`bg-white p-4 sm:p-5 rounded-xl shadow-sm border border-gray-100 min-w-0 ${className}`}>
      <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-2">
        <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Individual Performance</h4>
        {(() => {
          const unit = data.unit_highlights.find(u => u.unit_name === selectedUnit);
          if (!unit) return null;
          const currentMonthIssues = parseFloat(unit.current_month_issues) || 0;
          const fleetAverage = parseFloat(data.summary?.fleet_average) || 15.4;

          let status = 'Average';
          let statusColor = 'text-blue-600';
          if (currentMonthIssues < fleetAverage * 0.85) {
            status = 'Excellent';
            statusColor = 'text-green-600';
          } else if (currentMonthIssues > fleetAverage * 1.15) {
            status = 'Needs Attention';
            statusColor = 'text-red-600';
          }
          return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusColor} bg-opacity-10 border border-current`}>{status}</span>;
        })()}
      </div>
      {(() => {
        const chartData = getIndividualUnitChartData();
        if (!chartData) return null;
        if (chartData.isEmpty) {
          return (
            <div className="flex items-center justify-center h-80 text-gray-400">
              <div className="text-center">
                <Activity className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{chartData.message}</p>
              </div>
            </div>
          );
        }
        return (
          <Chart
            key={`individual-${selectedUnit}`}
            data={chartData.data}
            type={chartData.type}
            options={chartData.options}
          />
        );
      })()}
    </div>
  );

  const renderFleetComparisonCard = (className = '') => (
    <div className={`bg-white p-4 sm:p-5 rounded-xl shadow-sm border border-gray-100 min-h-[400px] min-w-0 ${className}`}>
      <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide border-b border-gray-100 pb-2">Fleet Comparison</h4>
      {(() => {
        const chartData = getFleetComparisonChartData();
        if (!chartData) return null;
        if (chartData.isEmpty) {
          return (
            <div className="flex items-center justify-center h-80 text-gray-400">
              <div className="text-center">
                <Activity className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{chartData.message}</p>
              </div>
            </div>
          );
        }
        return (
          <div className="h-80">
            <Chart
              key={`fleet-${selectedUnit}`}
              data={chartData.data}
              type={chartData.type}
              options={chartData.options}
            />
          </div>
        );
      })()}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Control Panel */}
      <div
        className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-xl border"
        style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}
      >
        <div className="flex items-center space-x-3 w-full sm:w-auto">
          <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-100 text-gray-400 hidden sm:block">
            <Calendar className="h-4 w-4" />
          </div>
          <SearchableDropdown
            options={data.unit_highlights.map(unit => ({
              label: unit.unit_name,
              value: unit.unit_name,
              count: unit.current_month_issues
            }))}
            value={selectedUnit}
            onChange={setSelectedUnit}
            label="Select Unit"
          />
        </div>

        <div className="flex items-center space-x-2 text-sm text-gray-500 bg-white px-3 py-1.5 rounded-lg border border-gray-100 shadow-sm">
          <Calendar className="h-3.5 w-3.5" />
          <span>
            {(() => {
              const formatPeriod = (period) => {
                if (!period) return '';
                const parts = period.split('-');
                if (parts.length !== 2) return period;
                const month = parseInt(parts[1], 10);
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                return `${monthNames[month - 1]} ${parts[0]}`;
              };

              // Use selected unit's date range, or fall back to first unit's range
              if (data.unit_monthly_data) {
                const unitData = data.unit_monthly_data[selectedUnit] || data.unit_monthly_data[Object.keys(data.unit_monthly_data)[0]];
                if (unitData && unitData.period_start && unitData.period_end) {
                  return `${formatPeriod(unitData.period_start)} - ${formatPeriod(unitData.period_end)}`;
                }
              }
              return "Aug 2024 - Dec 2025";
            })()}
          </span>
        </div>
      </div>

      {/* Charts Section - carousel on mobile, side-by-side on desktop */}
      <div className="lg:hidden space-y-3">
        <div
          ref={chartsCarouselRef}
          onScroll={handleChartsCarouselScroll}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-1 px-1 pb-2"
        >
          <div data-carousel-item="monthly-chart" className="w-full min-w-full snap-start shrink-0">
            {renderIndividualPerformanceCard()}
          </div>
          <div data-carousel-item="monthly-chart" className="w-full min-w-full snap-start shrink-0">
            {renderFleetComparisonCard()}
          </div>
        </div>
        <div className="flex items-center justify-center gap-2">
          {[0, 1].map((idx) => (
            <button
              key={`monthly-chart-dot-${idx}`}
              type="button"
              onClick={() => scrollToChart(idx)}
              aria-label={`Go to monthly chart ${idx + 1}`}
              className={`h-2 rounded-full transition-all ${mobileChartIndex === idx ? 'w-5 bg-blue-500' : 'w-2 bg-gray-300'}`}
            />
          ))}
        </div>
      </div>

      <div className="hidden lg:grid grid-cols-2 gap-6">
        {renderIndividualPerformanceCard()}
        {renderFleetComparisonCard()}
      </div>

      {/* Unit Summary */}
      {/* Unit Summary */}
      <div>
        <h4 className="font-bold text-gray-900 mb-4 flex items-center">
          <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded mr-2">{selectedUnit}</span>
          Performance Summary
        </h4>
        {(() => {
          const unit = data.unit_highlights.find(u => u.unit_name === selectedUnit);
          const unitMonthlyData = data.unit_monthly_data?.[selectedUnit];

          if (!unit) return null;

          const currentMonthIssues = parseFloat(unit.current_month_issues) || 0;
          const yearlyAverage = parseFloat(unitMonthlyData?.yearly_average) || parseFloat(unit.monthly_average) || 0;
          const fleetAverage = parseFloat(data.monthly_averages?.fleet_average) ||
            parseFloat(data.summary?.fleet_average) ||
            15.4;

          // Performance ranking based on volume vs fleet
          let performance = 'Average';
          let performanceColor = 'text-blue-600';
          let performanceBg = 'bg-blue-50';

          if (currentMonthIssues < fleetAverage * 0.85) {
            performance = 'Excellent';
            performanceColor = 'text-green-600';
            performanceBg = 'bg-green-50';
          } else if (currentMonthIssues > fleetAverage * 1.15) {
            performance = 'Needs Attention';
            performanceColor = 'text-red-600';
            performanceBg = 'bg-red-50';
          }

          // Determine the month name for the label
          const getMonthName = (period) => {
            if (!period) return 'Current Month';
            const parts = period.split('-');
            if (parts.length !== 2) return 'Current Month';
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            const monthIdx = parseInt(parts[1], 10) - 1;
            return monthNames[monthIdx] || 'Current Month';
          };

          const currentMonthName = getMonthName(unitMonthlyData?.period_end);

          return (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm relative overflow-hidden group hover:border-blue-200 transition-colors">
                <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Calendar className="w-12 h-12 text-blue-600" />
                </div>
                <div className="text-sm text-gray-500 font-medium mb-1">{currentMonthName}</div>
                <div className="text-3xl font-bold text-gray-900">{currentMonthIssues}</div>
                <div className="text-xs text-gray-400 mt-1">Events count</div>
              </div>

              <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm relative overflow-hidden group hover:border-purple-200 transition-colors">
                <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                  <TrendingUp className="w-12 h-12 text-purple-600" />
                </div>
                <div className="text-sm text-gray-500 font-medium mb-1">Yearly Average</div>
                <div className="text-3xl font-bold text-gray-900">{yearlyAverage.toFixed(1)}</div>
                <div className="text-xs text-gray-400 mt-1">Events / month</div>
              </div>

              <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm relative overflow-hidden group hover:border-indigo-200 transition-colors">
                <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Users className="w-12 h-12 text-indigo-600" />
                </div>
                <div className="text-sm text-gray-500 font-medium mb-1">Fleet Average</div>
                <div className="text-3xl font-bold text-gray-900">{fleetAverage.toFixed(1)}</div>
                <div className="text-xs text-gray-400 mt-1">Benchmark</div>
              </div>

              <div className={`p-4 rounded-xl border shadow-sm relative overflow-hidden ${performanceBg} border-transparent`}>
                <div className="flex items-center justify-between relative z-10">
                  <div>
                    <div className="text-sm font-medium opacity-80 mb-1 text-gray-900">Performance Status</div>
                    <div className={`text-xl font-bold ${performanceColor} flex items-center mb-1`}>
                      {performance}
                    </div>
                    <div className="flex items-center text-xs opacity-70 font-medium text-gray-800">
                      Trends
                    </div>
                  </div>
                  <div className={`p-2 rounded-lg bg-white/50 ${performanceColor}`}>
                    <Activity className="w-6 h-6" />
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default MonthlyTrendsRenderer;
